"use client";

import * as React from "react";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useRef, startTransition } from "react";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { usePathname } from "next/navigation";
// Logo component — back strokes use primary (theme), front strokes use foreground (typography)
const Logo = ({ className, ...props }: React.SVGAttributes<SVGElement>) => (
  <svg
    className={className}
    width={50}
    height={50}
    viewBox="0 0 512 512"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-hidden="true"
    {...props}
  >
    <rect style={{ fill: "var(--primary)" }} opacity="0.6" width="53.539825" height="411.46637" x="-177.97433" y="62.13142" transform="matrix(-1,0,0.23443029,0.97213293,0,0)" />
    <rect style={{ fill: "var(--primary)" }} opacity="0.6" width="53.539825" height="411.46637" x="-371.97433" y="62.13142" transform="matrix(-1,0,0.23443029,0.97213293,0,0)" />
    <rect style={{ fill: "var(--foreground)" }} width="53.539825" height="413.41089" x="152.54514" y="60.366219" transform="matrix(1,0,-0.23332763,0.97239818,0,0)" />
    <rect style={{ fill: "var(--foreground)" }} width="53.539825" height="413.41089" x="346.54544" y="60.366219" transform="matrix(1,0,-0.23332763,0.97239818,0,0)" />
  </svg>
);

// Hamburger icon component
const HamburgerIcon = ({
  className,
  ...props
}: React.SVGAttributes<SVGElement>) => (
  <svg
    className={cn("pointer-events-none", className)}
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M4 12L20 12"
      className="origin-center -translate-y-[7px] transition-all duration-(--dur-base) ease-(--ease-morph) group-aria-expanded:translate-x-0 group-aria-expanded:translate-y-0 group-aria-expanded:rotate-[315deg]"
    />
    <path
      d="M4 12H20"
      className="origin-center transition-all duration-(--dur-base) ease-(--ease-morph) group-aria-expanded:rotate-45"
    />
    <path
      d="M4 12H20"
      className="origin-center translate-y-[7px] transition-all duration-(--dur-base) ease-(--ease-morph) group-aria-expanded:translate-y-0 group-aria-expanded:rotate-[135deg]"
    />
  </svg>
);

// Types
export interface NavbarNavLink {
  href: string;
  label: string;
}

export interface NavbarProps extends React.HTMLAttributes<HTMLElement> {
  navigationLinks?: NavbarNavLink[];
  ctaText?: string;
  ctaHref?: string;
}

// Default navigation links
const defaultNavigationLinks: NavbarNavLink[] = [
  { href: "/", label: "Home" },
  { href: "/projects", label: "Projects" },
  { href: "/blog", label: "Blog" },
  { href: "/resume", label: "Résumé" },
];

export const Navbar = React.forwardRef<HTMLElement, NavbarProps>(
  (
    {
      className,
      navigationLinks = defaultNavigationLinks,
      ctaText = "Contact",
      ctaHref = "/contact",
      ...props
    },
    ref,
  ) => {
    const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const containerRef = useRef<HTMLElement>(null);
    const firstMobileLinkRef = useRef<HTMLAnchorElement>(null);

    // Focus the first mobile nav link once, when the menu transitions to open —
    // not on every re-render, or keyboard focus gets yanked back mid-navigation.
    useEffect(() => {
      if (mobileMenuOpen) {
        firstMobileLinkRef.current?.focus();
      }
    }, [mobileMenuOpen]);

    useEffect(() => {
      const checkWidth = () => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth;
          setIsMobile(width < 768); // 768px is md breakpoint
        }
      };

      checkWidth();

      const resizeObserver = new ResizeObserver(checkWidth);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    // Combine refs
    const combinedRef = React.useCallback(
      (node: HTMLElement | null) => {
        containerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const pathname = usePathname();

    // Close mobile menu on navigation
    useEffect(() => {
      startTransition(() => {
        setMobileMenuOpen(false);
      });
    }, [pathname]);

    return (
      <header
        ref={combinedRef}
        className={cn(
          "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 md:px-6 [&_*]:no-underline print:hidden",
          className,
        )}
        {...props}
      >
        <div className="container mx-auto flex h-16 max-w-screen-2xl items-center justify-between gap-4">
          {/* Left side */}
          <div className="flex items-center gap-2">
            {/* Mobile menu trigger */}
            <Popover modal open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <PopoverTrigger asChild aria-label="Navigation menu" className={isMobile === false ? "invisible" : "md:invisible"}>
                <Button
                  className="group h-9 w-9 hover:bg-accent hover:text-accent-foreground"
                  variant="ghost"
                  size="icon"
                  data-faro-user-action-name="open-mobile-menu"
                >
                  <HamburgerIcon aria-hidden="true" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-48 p-2"
                aria-label="Navigation menu"
              >
                <nav aria-label="Mobile navigation">
                  <ul className="flex flex-col gap-1 list-none m-0 p-0">
                    {navigationLinks.map((link, index) => (
                      <li key={index}>
                        <Link
                          ref={index === 0 ? firstMobileLinkRef : undefined}
                          href={link.href}
                          aria-current={pathname === link.href ? "page" : undefined}
                          data-active={pathname === link.href ? "true" : undefined}
                          data-faro-user-action-name={`nav-${link.label.toLowerCase()}`}
                          className={cn(
                            "flex w-full items-center rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground no-underline outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-2 focus-visible:outline-transparent",
                            pathname === link.href
                              ? "bg-accent text-accent-foreground"
                              : "text-foreground/80",
                          )}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </PopoverContent>
            </Popover>
            {/* Main nav */}
            <div className="flex items-center gap-6">
              <div className="flex items-center space-x-2 font-bold text-xl">
                <Logo />
                <span>malott.dev</span>
              </div>
              {/* Navigation menu */}
              {isMobile !== true && (
                <NavigationMenu className="hidden md:flex">
                  <NavigationMenuList className="gap-1">
                    {navigationLinks.map((link, index) => (
                      <NavigationMenuItem key={index}>
                        <Button
                          asChild
                          variant={pathname === link.href ? "default" : "ghost"}
                          size="sm"
                        >
                          <NavigationMenuLink
                            active={pathname === link.href}
                            href={link.href}
                            aria-current={pathname === link.href ? "page" : undefined}
                            data-faro-user-action-name={`nav-${link.label.toLowerCase()}`}
                            className="cursor-pointer no-underline"
                          >
                            {link.label}
                          </NavigationMenuLink>
                        </Button>
                      </NavigationMenuItem>
                    ))}
                  </NavigationMenuList>
                </NavigationMenu>
              )}
            </div>
          </div>
          {/* Right side */}
          <div className="flex items-center">
            <Button asChild data-faro-user-action-name="nav-cta">
              <Link href={ctaHref}>
                {ctaText}
              </Link>
            </Button>
          </div>
        </div>
      </header>
    );
  },
);

Navbar.displayName = "Navbar";

export { HamburgerIcon };
